import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command.jsx';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils.js';

// Searchable, keyboard-navigable template picker built on cmdk.
// Per TRACON Vision §V2 fix #3 (2026-05-20). Replaces the prior static
// scrolling list. Recommendations from recommendedForContext() pin at
// the top under "Suggested for this stage".
//
// Filters out broker-internal templates (toBroker: true) — these are
// system-triggered notifications (broker-notification, ready-to-quote)
// whose recipient resolves to BROKER_NOTIFICATION_EMAIL. (Band 0 polish
// 2026-05-20.)

const CATEGORY_LABEL = {
  applicant: 'Applicant / Client',
  pilot: 'Pilot',
  renewal: 'Renewal',
  client: 'Client',
  broker: 'Internal',
  certificate: 'Certificates',
  notice: 'Notices',
};

export default function TemplatePicker({
  templates,
  value,
  onSelect,
  recommendedSlugs = [],
}) {
  // Local search state — used to filter the list. cmdk also handles
  // arrow-key nav and Enter-to-select against the rendered items.
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  // Auto-focus the search on first mount so keyboard users can start
  // typing immediately when the ComposeSheet opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const composable = useMemo(
    () => (templates || []).filter((t) => !t.toBroker),
    [templates]
  );

  const recommendedSet = useMemo(
    () => new Set(recommendedSlugs || []),
    [recommendedSlugs]
  );

  const recommended = useMemo(
    () =>
      // Preserve the order from recommendedSlugs so the PRIMARY pick
      // (index 0) renders first in the Suggested group.
      (recommendedSlugs || [])
        .map((slug) => composable.find((t) => t.slug === slug))
        .filter(Boolean),
    [recommendedSlugs, composable]
  );

  const restByCategory = useMemo(() => {
    const groups = {};
    for (const t of composable) {
      if (recommendedSet.has(t.slug)) continue;
      const cat = t.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    }
    return groups;
  }, [composable, recommendedSet]);

  if (!templates || templates.length === 0) {
    return (
      <p className="rounded-card border border-border bg-bg/40 p-3 text-sm text-text-muted">
        No templates available for this deal.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-card border border-border bg-surface">
      {/* `filter` prop turns cmdk's built-in fuzzy match on across label +
          category + slug. shouldFilter:true (default) keeps the typed
          input narrowing visible items in real time. */}
      <Command
        // The default cmdk scoring sorts by relevance — keeps recommended
        // pinned in their own group regardless of typed input.
        label="Email templates"
      >
        <CommandInput
          ref={inputRef}
          placeholder="Search templates…"
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          <CommandEmpty>No matching template.</CommandEmpty>
          {recommended.length > 0 ? (
            <CommandGroup heading="Suggested for this stage">
              {recommended.map((tpl) => (
                <Row
                  key={tpl.slug}
                  tpl={tpl}
                  selected={value === tpl.slug}
                  onSelect={onSelect}
                  recommended
                />
              ))}
            </CommandGroup>
          ) : null}
          {Object.entries(restByCategory).map(([cat, items]) => (
            <CommandGroup
              key={cat}
              heading={CATEGORY_LABEL[cat] || cat}
            >
              {items.map((tpl) => (
                <Row
                  key={tpl.slug}
                  tpl={tpl}
                  selected={value === tpl.slug}
                  onSelect={onSelect}
                />
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </Command>
    </div>
  );
}

function Row({ tpl, selected, onSelect, recommended }) {
  // cmdk passes the literal item value to onSelect — set it to the slug
  // so handler doesn't need to look up the tpl by index.
  // `keywords` widens the cmdk search match to include audience + slug
  // so typing "pilot" matches pilot-reminder, etc.
  const keywords = [
    tpl.label,
    tpl.slug,
    tpl.audience,
    tpl.category,
  ].filter(Boolean);
  return (
    <CommandItem
      value={tpl.slug}
      keywords={keywords}
      onSelect={() => onSelect(tpl.slug)}
      data-selected={selected ? 'true' : undefined}
      className={cn(
        selected ? 'bg-accent-soft text-accent' : null
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium">
            {tpl.label}
          </span>
          {recommended ? (
            <span className="rounded-pill bg-accent-soft px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-accent">
              Suggested
            </span>
          ) : null}
        </div>
        {tpl.audience || tpl.category ? (
          <div className="text-[10px] text-text-dim">
            {[tpl.category, tpl.audience?.replace(/_/g, ' ')]
              .filter(Boolean)
              .join(' · ')}
          </div>
        ) : null}
      </div>
      {selected ? <Check size={14} className="text-accent" /> : null}
    </CommandItem>
  );
}
